import {
  checkPermissionForNavItem,
  navBarConfig
} from "common/components/Navbar";
import { useAuthContext } from "common/contexts/AuthContext";
import { getUserRole, isRoleGranite } from "common/utils";
import { getPermissionStrategy } from "common/utils/permissionStrategies";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const getFirstAuthorizedNav = (parsedRoleConfig, userRole, graniteSettings) => {
  // =========================
  // GRANITE USERS
  // =========================
  if (isRoleGranite(userRole)) {
    // History first (explicit priority)
    const historyItem = navBarConfig.find(
      (item) =>
        item.graniteKey === "history" &&
        graniteSettings.history === true &&
        checkPermissionForNavItem(
          item.permission,
          item.strategy,
          parsedRoleConfig,
          userRole
        )
    );

    if (historyItem) {
      return historyItem.component.to;
    }

    //  First enabled granite item (original order preserved)
    const firstGraniteItem = navBarConfig.find(
      (item) =>
        item.graniteKey &&
        graniteSettings[item.graniteKey] === true &&
        checkPermissionForNavItem(
          item.permission,
          item.strategy,
          parsedRoleConfig,
          userRole
        )
    );

    return firstGraniteItem?.component?.to || "/dashboard";
  }

  // =========================
  // RMPM USERS
  // =========================
  // Orders first (without moving it in config)
  const ordersItem = navBarConfig.find(
    (item) =>
      item.component?.keyItem === "orders" &&
      checkPermissionForNavItem(
        item.permission,
        item.strategy,
        parsedRoleConfig,
        userRole
      )
  );

  if (ordersItem) {
    return ordersItem.component.to;
  }

  // =========================
  // FALLBACK
  // =========================
  const firstAuthorizedItem = navBarConfig.find((item) =>
    checkPermissionForNavItem(
      item.permission,
      item.strategy,
      parsedRoleConfig,
      userRole
    )
  );

  return firstAuthorizedItem?.component?.to || "/dashboard";
};

const useCheckAuthorization = (
  permissions,
  strategy = "simple",
  graniteKey = null
) => {
  const { parsedRoleConfig, authConfig } = useAuthContext();
  const navigate = useNavigate();
  const checkIfAuthorized = getPermissionStrategy(strategy);

  useEffect(() => {
    const hasPermission = checkIfAuthorized(parsedRoleConfig, permissions);

    const graniteSettings = authConfig?.user?.graniteSettings || {};
    const hasGraniteAccess = graniteKey
      ? graniteSettings[graniteKey] === true
      : true;

    if (!hasPermission || !hasGraniteAccess) {
      const redirectTo = getFirstAuthorizedNav(
        parsedRoleConfig,
        getUserRole(authConfig),
        graniteSettings
      );
      navigate(redirectTo, { replace: true });
    }
  }, [
    parsedRoleConfig,
    authConfig,
    permissions,
    strategy,
    graniteKey,
    navigate
  ]);

  return {};
};

export default useCheckAuthorization;
